import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {Subscription} from 'rxjs';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {AppointmentType, QueryPatient, Appointment, AppointmentMetadata, CalendarAppointment, CalendarAppointmentPatient, instantToPracticeWallDate, pickerToWallClock, PracticeTimezoneService} from '@xmedical/core';
import {XmedicalUiModule} from '@xmedical/ui';
import {CALENDAR_ACTION_HANDLER, CALENDAR_DATA_PROVIDER} from '../../tokens/calendar.tokens';
import dayjs from 'dayjs/esm';

function endAfterStartValidator(control: AbstractControl): ValidationErrors | null {
  const start = control.get('date')?.value;
  const end = control.get('endDate')?.value;
  if (!start || !end) return null;

  const startDate = dayjs(start);
  const endDate = dayjs(end);
  if (!startDate.isValid() || !endDate.isValid()) return null;

  if (endDate.valueOf() < startDate.valueOf()) {
    return {endBeforeStart: true};
  }
  return null;
}

@Component({
  selector: 'app-appointment-edit-form',
  templateUrl: './appointment-edit-form.component.html',
  styleUrls: ['./appointment-edit-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    XmedicalUiModule,
  ]
})
export class AppointmentEditFormComponent implements OnInit, OnDestroy {
  @Input({required: true}) appointment: CalendarAppointment;
  @Input({required: true}) appointmentTypes: AppointmentType[];
  @Output() saved = new EventEmitter<CalendarAppointment>();
  @Output() cancelled = new EventEmitter<void>();

  private dialog = inject(MatDialog);
  private formBuilder = inject(UntypedFormBuilder);
  private dataProvider = inject(CALENDAR_DATA_PROVIDER);
  private actionHandler = inject(CALENDAR_ACTION_HANDLER);
  private practiceTz = inject(PracticeTimezoneService);

  validate = false;
  form: UntypedFormGroup;
  selectedDate: Date = new Date();
  patient?: QueryPatient | CalendarAppointmentPatient = null;
  selectedType: AppointmentType = null;
  saving = false;

  private createPatientDialogSubscription: Subscription;
  private appointmentsRepositorySubscription: Subscription;
  private formSubscription: Subscription;
  private dateChangeSubscription: Subscription;

  ngOnInit(): void {
    // appointment.start/end are zone-less practice-local; show them in the
    // (timezone-unaware) picker as practice wall-clock, and send that wall-clock
    // verbatim on save (pickerToWallClock). No-op for an in-zone viewer.
    const zone = this.practiceTz.practiceTimezone();
    this.selectedDate = instantToPracticeWallDate(this.appointment.start, zone);
    this.selectedType = this.appointmentTypes.find(t => t.id === this.appointment.appointmentType?.id) ?? null;
    this.patient = this.appointment.patient ?? null;

    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const endDate = this.appointment.end
      ? instantToPracticeWallDate(this.appointment.end, zone)
      : new Date(this.selectedDate.getTime() + defaultDuration * 60 * 1000);

    this.form = this.formBuilder.group({
      id: [this.appointment.id],
      title: [this.appointment.title, Validators.maxLength(100)],
      patientId: [this.patient?.id],
      date: [this.selectedDate, Validators.required],
      endDate: [endDate, Validators.required],
      comment: [this.appointment.comment, Validators.maxLength(250)],
      appointmentType: [this.selectedType.id, Validators.required],
      appointmentMetaData: this.formBuilder.group({
        name: [this.appointment.metaData?.name],
        cell: [this.appointment.metaData?.cell],
        email: [this.appointment.metaData?.email],
      })
    }, {validators: endAfterStartValidator});
    this.formSubscription = this.form.get('appointmentType').valueChanges.subscribe(val => {
      this.selectedType = this.appointmentTypes.find(t => t.id === val);
    });

    // When start changes, shift end to maintain duration
    this.dateChangeSubscription = this.form.get('date').valueChanges.subscribe(newStart => {
      if (!newStart) return;
      const currentStart = dayjs(this.selectedDate);
      const currentEnd = dayjs(this.form.get('endDate').value);
      if (currentStart.isValid() && currentEnd.isValid()) {
        const durationMs = currentEnd.valueOf() - currentStart.valueOf();
        this.form.get('endDate').setValue(dayjs(newStart).add(durationMs, 'millisecond').toDate(), {emitEvent: false});
      }
      this.selectedDate = dayjs(newStart).toDate();
    });
  }

  ngOnDestroy(): void {
    this.appointmentsRepositorySubscription?.unsubscribe();
    this.createPatientDialogSubscription?.unsubscribe();
    this.formSubscription?.unsubscribe();
    this.dateChangeSubscription?.unsubscribe();
  }

  save(): void {
    if (this.form.valid) {
      this.saving = true;
      const appointmentDetails = this.form.value;
      // Appointment times are zone-less wall-clock; send the wall the user picked
      // verbatim (never a converted instant).
      const appointment = {
        id: appointmentDetails.id,
        title: appointmentDetails.title,
        patientId: appointmentDetails.patientId,
        start: pickerToWallClock(appointmentDetails.date),
        end: pickerToWallClock(appointmentDetails.endDate),
        comment: appointmentDetails.comment,
        practiceId: this.dataProvider.selectedPracticeId(),
        deleted: false,
        appointmentType: this.selectedType,
        appointmentMetaData: {
          name: appointmentDetails.appointmentMetaData.name,
          cell: appointmentDetails.appointmentMetaData.cell,
          email: appointmentDetails.appointmentMetaData.email,
        }
      } as Appointment;
      this.appointmentsRepositorySubscription = this.dataProvider.updateAppointment(appointment).subscribe(() => {
        this.saving = false;
        const updated: CalendarAppointment = {
          ...this.appointment,
          title: appointment.title,
          start: appointment.start,
          end: appointment.end,
          comment: appointment.comment,
          metaData: {
            name: appointment.appointmentMetaData?.name,
            cell: appointment.appointmentMetaData?.cell,
            email: appointment.appointmentMetaData?.email,
          },
          appointmentType: this.selectedType && this.appointment.appointmentType
            ? {
                ...this.appointment.appointmentType,
                id: this.selectedType.id,
                title: this.selectedType.title,
                description: this.selectedType.description,
                displayStyle: this.selectedType.displayStyle,
                isPatientAppointment: this.selectedType.isPatientAppointment,
                canCreate: this.selectedType.canCreate,
              }
            : this.appointment.appointmentType,
        };
        this.saved.emit(updated);
      }, error => {
        console.error(error);
        this.saving = false;
      });
    }
    this.validate = true;
  }

  get appointmentMetaData(): UntypedFormGroup {
    return this.form.get('appointmentMetaData') as UntypedFormGroup;
  }

  get hasPatientMetaData(): boolean {
    return this.appointmentMetaData.value.name !== '';
  }

  get appointmentMetaPatientData(): AppointmentMetadata {
    return this.appointmentMetaData.value as AppointmentMetadata;
  }

  get hasPatient(): boolean {
    return this.patient !== null;
  }

  get allowPatientAppointmentsWithoutPatient(): boolean {
    return this.dataProvider.practiceSettings()?.allowPatientAppointmentsWithoutPatient ?? false;
  }

  addNewPatient(): void {
    this.createPatientDialogSubscription = this.actionHandler.onCreatePatientRequested(this.appointment).subscribe((patient: QueryPatient) => {
      if (patient) {
        this.patient = patient;
        this.form.get('patientId').setValue(patient.id);
        this.form.get('patientId').updateValueAndValidity();
        this.form.get('title').setValue(patient.formalName ?? patient.name ?? this.form.get('title').value);
        this.form.get('title').updateValueAndValidity();
      }
    });
  }
}
